/**
 * @author Tanmay <tanmay.pawar@314ecorp.com>
 * @description constant for app
 */

import _ from 'lodash';
import { ThemeConfig } from 'antd';

import dayjs from 'utils/dayjs';
import { LokiLogInfo } from 'client/api';

export const MESSAGE_DISPLAY_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export const DEFAULT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

export const DEFAULT_TIME_FORMAT = 'HH:mm';

export const CONNECTOR_DIRECTORY_INITIAL_PATH = '/';

export const keycloakRoles = {
	ADMIN: '_admin',
	DEFAULT_USER: 'manage-default-users',
	MANAGE_CONNECTOR: 'manage-connector',
	MANAGE_CREDENTIALS: 'manage-credentials',
	MANAGE_INTERFACE_MIGRATION: 'manage-interface-migrations',
	MANAGE_LIBRARIES: 'manage-libraries',
	MANAGE_MESSAGES: 'manage-messages',
	MANAGE_TASKS: 'manage-tasks',
	MANAGE_METRIC_DASHBOARD: 'manage-metric-dashboard',
};

export const disabledRouteComponent = [
	'zookeeper',
	'zendesk',
	'zeebe',
	'xmpp',
	'whatsapp',
	'workday',
	'xchange',
	'weather',
	'twitter-search',
	'undertow',
	'twitter-timeline',
	'twitter-directmessage',
	'twilio',
	'telegram',
	'stub',
	'spring-batch',
	'spring-event',
	'spring-jdbc',
	'spring-ldap',
	'spring-rabbitmq',
	'spring-redis',
	'spring-ws',
	'slack',
	'salesforce',
	'sap-netweaver',
	'robotframework',
	'quickfix',
	'pulsar',
	'pubnub',
	'lpr',
	'plc4x',
	'paho-mqtt5',
	'paho',
	'optaplanner',
	'openstack-swift',
	'openstack-nova',
	'openstack-keystone',
	'openstack-neutron',
	'olingo4',
	'olingo2',
	'netty-http',
	'netty',
	'jira',
	'dropbox',
	'digitalocean',
	'dhis2',
	'box',
	'chatscript',
	'openshift',
	'openstack',
	'kubernetes',
	'ignite',
	'hwcloud',
];

export const roleOptions = [
	{ label: 'Admin', value: keycloakRoles.ADMIN },

	{
		label: 'Default User',
		value: keycloakRoles.DEFAULT_USER,
	},
	{
		label: 'Can Manage Connector',
		value: keycloakRoles.MANAGE_CONNECTOR,
	},
	{
		label: 'Can Manage Credentials',
		value: keycloakRoles.MANAGE_CREDENTIALS,
	},
	{
		label: 'Can Manage Interface Migration',
		value: keycloakRoles.MANAGE_INTERFACE_MIGRATION,
	},
	{
		label: 'Can Manage Libraries',
		value: keycloakRoles.MANAGE_LIBRARIES,
	},
	{
		label: 'Can Manage Messages',
		value: keycloakRoles.MANAGE_MESSAGES,
	},

	{
		label: 'Can Manage Tasks',
		value: keycloakRoles.MANAGE_TASKS,
	},
	{
		label: 'Can Manage Metric Dashboard',
		value: keycloakRoles.MANAGE_METRIC_DASHBOARD,
	},
];
export const keyedRoles = _.keyBy(roleOptions, 'value');

export const APPLOGS_DATEFORMAT = 'YYYY-MM-DD HH:mm:ss';

export const antdTheme: ThemeConfig = {
	token: {
		colorPrimary: '#044FB3',
		colorLink: '#044FB3',
		colorLinkHover: '#5B47B6',
		colorBgContainerDisabled: '#FFF',
		fontWeightStrong: 500,
	},
	components: {
		Table: {
			borderColor: '#f0f0f0',
			headerBg: '#f9f9fb',
			padding: 16,
			paddingContentVerticalLG: 12,
			rowHoverBg: '#fdf4e6 !important',
			bodySortBg: 'transparent',
			rowSelectedBg: '#fff',
		},
		Typography: {
			titleMarginBottom: 0,
			titleMarginTop: 0,
		},
		Divider: {
			colorSplit: '#E2E8F0',
		},
	},
};

export const GROOVY_EXECUTION_CLASS = 'com.e314.zsegment.api.scheduler.job.GroovyScriptExecutingJob';
export const TASK_DIRECTORY = 'tasks';

export const DATE_FORMAT_FRACTIONAL_SECONDS = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

export const TASK_TYPES = {
	predefined: 'Predefined',
	custom: 'Custom',
};

export const GROOVY_TASK = 'GroovyScriptExecutingJob';

export const RECORD_DATA_TYPES = ['STRING', 'INTEGER', 'DOUBLE', 'DATE_TIME'];

export const kindToLanguageMapping = {
	mappings: 'yaml',
	schema: 'xml',
	routes: 'yaml',
	script: 'java',
};
export const DATE_FORMAT = 'YYYY--MM-DD';

export const excludedFolderList = ['common', 'functions', 'tasks'];

export const INTERFACE_MIGRATION_FILES_TO_IGNORE = {
	SCHEMA_FILE: 'schemas.json',
	CUSTOM_JAR_FILE: 'customJars.json',
	INTERFACE_PROTOCOL_CONNECTOR_FILE: 'interface_protocols.json',
	GITIGNORE_PATTERN: '/.gitignore',
	JAR_PATTERN: 'jar/',
	COMMON_JAR_PATTERN: '/jar/',
	SCHEMA_CUSTOM_PATTERN: 'schema/custom/',
	SCHEMA_PREDEFINED_PATTERN: 'schema/predefined/',
	DATABASE_FILE: 'database.json',
	MANIFEST_FILE: 'manifest.json',
};

export const cronPattern =
	// eslint-disable-next-line no-useless-escape, max-len
	/^\s*($|#|\w+\s*=|(\?|\*|(?:[0-5]?\d)(?:(?:-|\/|\,)(?:[0-5]?\d))?(?:,(?:[0-5]?\d)(?:(?:-|\/|\,)(?:[0-5]?\d))?)*)\s+(\?|\*|(?:[0-5]?\d)(?:(?:-|\/|\,)(?:[0-5]?\d))?(?:,(?:[0-5]?\d)(?:(?:-|\/|\,)(?:[0-5]?\d))?)*)\s+(\?|\*|(?:[01]?\d|2[0-3])(?:(?:-|\/|\,)(?:[01]?\d|2[0-3]))?(?:,(?:[01]?\d|2[0-3])(?:(?:-|\/|\,)(?:[01]?\d|2[0-3]))?)*)\s+(\?|\*|(?:0?[1-9]|[12]\d|3[01])(?:(?:-|\/|\,)(?:0?[1-9]|[12]\d|3[01]))?(?:,(?:0?[1-9]|[12]\d|3[01])(?:(?:-|\/|\,)(?:0?[1-9]|[12]\d|3[01]))?)*)\s+(\?|\*|(?:[1-9]|1[012])(?:(?:-|\/|\,)(?:[1-9]|1[012]))?(?:L|W)?(?:,(?:[1-9]|1[012])(?:(?:-|\/|\,)(?:[1-9]|1[012]))?(?:L|W)?)*|\?|\*|(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(?:(?:-)(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))?(?:,(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(?:(?:-)(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))?)*)\s+(\?|\*|(?:[0-6])(?:(?:-|\/|\,|#)(?:[0-6]))?(?:L)?(?:,(?:[0-6])(?:(?:-|\/|\,|#)(?:[0-6]))?(?:L)?)*|\?|\*|(?:MON|TUE|WED|THU|FRI|SAT|SUN)(?:(?:-)(?:MON|TUE|WED|THU|FRI|SAT|SUN))?(?:,(?:MON|TUE|WED|THU|FRI|SAT|SUN)(?:(?:-)(?:MON|TUE|WED|THU|FRI|SAT|SUN))?)*)(|\s)+(\?|\*|(?:|\d{4})(?:(?:-|\/|\,)(?:|\d{4}))?(?:,(?:|\d{4})(?:(?:-|\/|\,)(?:|\d{4}))?)*))$/;

export const messageEncodingOptions = [
	{
		value: 'UTF-8',
		label: 'UTF-8',
	},
];

export const customFieldActionTemplate = `package custom_field_actions;
import java.io.Serializable;
import io.atlasmap.v2.*
import io.atlasmap.spi.AtlasFieldAction;
import io.atlasmap.spi.AtlasActionProcessor;

public class ClassName implements AtlasFieldAction {

    public static class ActionName extends Action implements Serializable {
        @AtlasActionProperty(title = "Action title", type = FieldType.STRING)
        public String exampleProperty = "-temp-";

    }

    @AtlasActionProcessor
    public static String actionName(ActionName action, String userInput) {
        return "example-" + action.exampleProperty + userInput;
    }

}
`;

export const HL7_FILETYPE = 'hl7';
export const X12_FILETYPE = 'x12';

export const formatAppLogs = (log: LokiLogInfo): string =>
	`${dayjs.utc(log.log_timestamp).local().format(APPLOGS_DATEFORMAT)} | ${log.log_line}`;

export const appOptions = [
	{ label: 'API', value: 'API' },
	{ label: 'ENGINE', value: 'ENGINE' },
	{ label: 'GROOVY ENGINE', value: 'GROOVY_ENGINE' },
	{ label: 'JOB HISTORY', value: 'JOB_HISTORY' },
];

export const logLevelOptions = [
	{ label: 'INFO', value: 'INFO' },
	{ label: 'WARN', value: 'WARN' },
	{ label: 'ERROR', value: 'ERROR' },
	{ label: 'DEBUG', value: 'DEBUG' },
];

export const DEFAULT_FIREWALL_RULE_NUMBER = 22;
export const DEFAULT_SSH_KEY =
	'ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIDYLMfHkNGJNkcStJlsbh8L3CL6lzk4xNN2Vm8l4Z7+w amit.agrawal@pop-os';

export const REGEX_PATTERNS = {
	// eslint-disable-next-line max-len
	URL: /^(https?):\/\/([a-zA-Z0-9.-]+)(?::\d{1,5})?(?:\/[a-zA-Z0-9_.-]*)*(?:\?[a-zA-Z0-9&=_-]*)?(?:#[a-zA-Z0-9_-]*)?$/,
	CERTIFICATE_NAME: /^[a-zA-Z][a-zA-Z0-9-]+$/,
	COMMON_NAME: /^[a-zA-Z_]\w+$/,
};

export const JAR = 'jar';

export const REFRESH_INTERVAL_OPTIONS = [
	{ label: 'Off', value: false },
	{ label: '5s', value: '5s' },
	{ label: '10s', value: '10s' },
	{ label: '30s', value: '30s' },
	{ label: '1m', value: '1m' },
	{ label: '5m', value: '5m' },
	{ label: '15m', value: '15m' },
	{ label: '30m', value: '30m' },
	{ label: '1h', value: '1h' },
	{ label: '2h', value: '2h' },
	{ label: '1d', value: '1d' },
];
